import { KeyboardEvent } from 'react';
import styled from 'styled-components';
import { ArdoqId } from '@ardoq/api-types';
import { Box, FlexBox, Inline, Stack } from '@ardoq/layout';
import { Text, TextColor } from '@ardoq/typography';
import { t, type I18nLocale } from '@ardoq/i18n';
import { SkeletonLoader } from '@ardoq/loader';
import { FormSize, SearchInput } from '@ardoq/forms';
import { DropdownMenuOption } from '@ardoq/dropdown-menu-ui';
import { IconSize, InfoIcon } from '@ardoq/icons';
import { colors } from '@ardoq/design-tokens';
import { WithPopover } from '@ardoq/popovers';
import {
  DocumentItemViewModel,
  OrgDocumentsPickerView,
} from '../assistantDocumentsOperations';
import { DocumentThumbnail } from './DocumentPreview';
import { DocumentListSkeleton } from './DocumentListSkeleton';

const LIST_MAX_HEIGHT = 280;

const ScrollArea = styled(Box)`
  max-height: ${LIST_MAX_HEIGHT}px;
  overflow-y: auto;
  overflow-x: hidden;
`;

export type DocumentSelectionProps = {
  selectedDocumentIds: Set<string>;
  isSelectionLimitReached: boolean;
  onSelectDocument: (item: DocumentItemViewModel) => void;
  onDeselectDocument: (documentId: ArdoqId) => void;
};

export type DocumentSearchProps = {
  value: string;
  onValueChange: (searchTerm: string) => void;
};

type OrgDocumentsPickerProps = DocumentSelectionProps & {
  i18nLocale: I18nLocale;
  orgDocumentsView: OrgDocumentsPickerView;
  search: DocumentSearchProps;
};

const CenteredMessage = ({
  textColor = 'textSubtle',
  children,
}: {
  textColor?: TextColor;
  children: string;
}) => (
  <FlexBox justify="center" align="center" padding="medium">
    <Text color={textColor}>{children}</Text>
  </FlexBox>
);

type DocumentListProps = DocumentSelectionProps & {
  items: DocumentItemViewModel[];
};

const DocumentList = ({
  items,
  selectedDocumentIds,
  isSelectionLimitReached,
  onSelectDocument,
  onDeselectDocument,
}: DocumentListProps) => (
  <ScrollArea role="listbox" aria-multiselectable="true">
    {items.map(item => {
      const isSelected = selectedDocumentIds.has(item.id);
      const isDisabled = isSelectionLimitReached && !isSelected;
      const toggle = () =>
        isSelected ? onDeselectDocument(item.id) : onSelectDocument(item);
      const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (isDisabled || (event.key !== 'Enter' && event.key !== ' ')) {
          return;
        }
        event.preventDefault();
        toggle();
      };
      return (
        <DropdownMenuOption
          key={item.id}
          label={item.name}
          truncateTitle
          ariaLabel={item.name}
          leftContent={<DocumentThumbnail item={item} />}
          isChecked={isSelected}
          isDisabled={isDisabled}
          onClick={toggle}
          menuOptionWrapperProps={{
            tabIndex: isDisabled ? -1 : 0,
            onKeyDown,
          }}
        />
      );
    })}
  </ScrollArea>
);

type PickerBodyProps = OrgDocumentsPickerProps;

const PickerBody = ({
  orgDocumentsView,
  i18nLocale,
  search,
  ...selectionProps
}: PickerBodyProps) => {
  if (orgDocumentsView.status === 'loading') {
    return (
      <SkeletonLoader
        message={t(
          i18nLocale,
          'assistant:documents.loading',
          'Loading documents'
        )}
      >
        <DocumentListSkeleton />
      </SkeletonLoader>
    );
  }

  if (orgDocumentsView.status === 'error') {
    return (
      <CenteredMessage textColor="textError">
        {t(
          i18nLocale,
          'assistant:documents.loadError',
          'Could not load documents'
        )}
      </CenteredMessage>
    );
  }

  if (orgDocumentsView.status === 'empty') {
    return (
      <CenteredMessage>
        {t(i18nLocale, 'assistant:documents.empty', 'No documents')}
      </CenteredMessage>
    );
  }

  return (
    <Stack gap="small">
      <SearchInput
        formSize={FormSize.FULL}
        value={search.value}
        onValueChange={search.onValueChange}
        placeholder={t(
          i18nLocale,
          'assistant:documents.searchPlaceholder',
          'Search…'
        )}
      />
      {orgDocumentsView.status === 'noResults' ? (
        <CenteredMessage>
          {t(i18nLocale, 'assistant:documents.noResults', 'No results')}
        </CenteredMessage>
      ) : (
        <DocumentList items={orgDocumentsView.items} {...selectionProps} />
      )}
    </Stack>
  );
};

export const OrgDocumentsPicker = (props: OrgDocumentsPickerProps) => (
  <Box padding="small">
    <Stack gap="small">
      <Box paddingX="small">
        <Inline gap="xsmall" align="center">
          <Text variant="text2" color="textSubtle">
            {t(
              props.i18nLocale,
              'assistant:documents.orgDocumentsHeader',
              'Organization documents'
            )}
          </Text>
          <WithPopover
            content={t(
              props.i18nLocale,
              'assistant:documents.supportedTypesInfo',
              'Only supported files are shown: PDF, JPEG, PNG, SVG, MD, CSV, HTML, TXT, JSON.'
            )}
          >
            <InfoIcon iconSize={IconSize.SMALL} color={colors.grey500} />
          </WithPopover>
        </Inline>
      </Box>
      <PickerBody {...props} />
    </Stack>
  </Box>
);
